import { createRoot } from 'react-dom/client';
import { AppContainer } from '~/AppContainer';
import SRError from '~/utils/errors/srError';
import getClientConfig from '~/utils/getClientConfig';
import { loadIndependentPlugins } from '~/utils/loadIndependentPlugins';
import loadPlugin from '~/utils/loadPlugin';
import loadTheme from '~/utils/loadTheme';

import 'serp-global-styles/globals.scss';

import { ISerpPlugin } from '~/plugins/SerpPlugin';
import { ErrorMessagesInit } from '~/utils/errors/errorMessages';
import { handleABTesting } from './abTesting/handleABTesting';
import { Configuration } from './core/configuration/configuration.type';
import { sentryInit } from './observability/sentry';
import { DynamicComponents } from './plugins/dynamicComponents/dynamicComponents';
import { loadDynamicComponents } from './plugins/dynamicComponents/loadDynamicComponents';

interface AppParameters {
    debug: string | null;
    clientConfigURL: string | null;
}

const getParametersFromQueryString = (string: string): AppParameters => {
    const params = new URLSearchParams(string);
    return {
        debug: params.get('debug'),
        clientConfigURL: params.get('clientConfigURL'),
    };
};

const pickParameter = (param: keyof AppParameters, preferred: AppParameters, fallback: AppParameters) => {
    if (preferred[param] !== null) {
        return preferred[param];
    }

    return fallback[param];
};

/**
 * Creates the root div for rendering and initializes the application.
 *
 * @remarks
 * This function initializes the application by:
 * - Getting configuration
 * - Loading theme
 * - Loading plugins
 * - Rendering the application
 *
 * @returns void
 * @throws SRError
 */
const initApplication = async () => {
    const scriptTag: HTMLScriptElement | undefined = Array.from(document.scripts).find((script: HTMLScriptElement) =>
        /searchready.bundle.js/.test(script.src)
    );

    if (!scriptTag) {
        throw new SRError(ErrorMessagesInit.NO_SCRIPT_TAG);
    }

    const rootDiv = document.createElement('div');
    rootDiv.setAttribute('id', 'searchready');
    document.body.prepend(rootDiv);

    const scriptSrc = scriptTag.getAttribute('src') as string;
    const scriptParameters = getParametersFromQueryString(scriptSrc.split('?')[1]);
    const urlParameters = getParametersFromQueryString(window.location.search);

    const clientConfigURL = (() => {
        if (
            /^(.*).localhost$/.test(window.location.hostname) ||
            /^(.*).mediafactory.dev$/.test(window.location.hostname)
        ) {
            return pickParameter('clientConfigURL', urlParameters, scriptParameters);
        }
        return scriptParameters['clientConfigURL'];
    })();
    const debug = pickParameter('debug', urlParameters, scriptParameters) === 'true';

    if (clientConfigURL === null) {
        throw new SRError(ErrorMessagesInit.NO_CONFIG_URL);
    }

    if (debug) {
        console.log('Config url', clientConfigURL);
    }

    try {
        const config = await getClientConfig(clientConfigURL);

        sentryInit(config?.observability?.sentry);

        const startSERP = handleABTesting(config);

        if (!startSERP) {
            console.log('AB testing is not in favor of starting SERP. Exiting.');
            return;
        }

        const pluginName = (() => {
            if (Array.isArray(config.layout.configuration.plugins)) {
                return config.layout.configuration.plugins[0]?.name;
            }
            return config.layout.configuration.plugins?.name;
        })();
        const theme = config.layout.configuration?.theme;

        if (debug) {
            console.log('Config', config);
        }

        loadTheme(theme, debug);
        loadIndependentPlugins({ config, debug });

        const dynamicComponentsPromise = loadDynamicComponents({ pluginName, debug });
        const pluginPromise = pluginName ? loadPlugin(pluginName, debug) : null;

        const [plugin, dynamicComponents] = await Promise.all([pluginPromise, dynamicComponentsPromise]);

        plugin?.beforeSerpRender(config);
        renderApplication({ config, plugin, dynamicComponents, debug });
    } catch (e) {
        console.error(e);
        throw new SRError(ErrorMessagesInit.INIT_GENERAL, e);
    }
};

const renderApplication = ({
    config,
    plugin,
    debug,
    dynamicComponents,
}: {
    config: Configuration;
    plugin: ISerpPlugin | null;
    debug: boolean;
    dynamicComponents: DynamicComponents;
}) => {
    const container = document.getElementById('searchready');
    if (!container) {
        throw new SRError(ErrorMessagesInit.NO_CONTAINER);
    }

    const root = createRoot(container);
    root.render(<AppContainer config={config} plugin={plugin} debug={debug} dynamicComponents={dynamicComponents} />);
};

export default initApplication;
